import AppsHeader from "@lib/components/AppComponents/AppsContainer/AppsHeader";
import AppTableContentHeader from "@lib/components/MiniComponents/Table/AppTableContentHeader";
import AppsContent from "@lib/components/AppComponents/AppsContainer/AppsContent";
import { TableHeaderFilter } from "@lib/types/Api";
import { useEffect, useMemo, useState } from "react";
import { useParamsManager } from "@lib/hooks/useParamsManager";
import { handleTableChange } from "@lib/helpers/UrlHelper";
import { TableProps } from "antd";
import { useIntl } from "react-intl";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AppsContainer from "@lib/components/AppComponents/AppsContainer";
import AppTable from "@lib/components/MiniComponents/Table/AppTable";
import { BillingPaymentRequestModal } from "@lib/components/PageComponents/PaymentRequest";
import { AppContext, SharedContextProps } from "@lib/types/Context";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  apiAddBillingHistories,
  apiAddBillingHistory,
  apiDeleteBillingHistory,
  apiGetBillingHistories,
} from "@lib/api/wallet";
import { BillingHistory, Customer } from "@lib/types";
import getColumns from "./columns";
import { useTypedSelector } from "@lib/redux";

type Props = SharedContextProps & {
  customer?: Customer;
};

const SharedBlockchainPaymentRequestList: React.FC<Props> = ({
  context,
  detailContext,
  customTitle,
  withoutContainer = false,
  customer,
  customerId,
  pageIsActive = true,
  minusHeight,
}) => {
  const { messages } = useIntl();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { type = context || AppContext.CUSTOMER } = useParams();
  const { role } = useTypedSelector((state) => state.auth.user);

  // Context-specific query parameters
  const initialFilters = [
    ...(state?.walletAddress ? [`address||$eq||${state?.walletAddress}`] : []),
    ...(customerId ? [`customerId||$eq||${customerId}`] : []),
  ];

  const { params, setParams, handleRemoveFilters } = useParamsManager({
    page: 1,
    limit: 100,
    sort: "createdAt,DESC",
    join: ["walletAddress", "customer"],
    filter: initialFilters,
  });

  const query = useQuery({
    queryFn: () => apiGetBillingHistories(params),
    queryKey: ["customer-billing-history", JSON.stringify(params)],
    enabled: !!params && pageIsActive,
    refetchInterval: 1000 * 60,
  });

  const { data, isLoading, isFetching, refetch } = query;
  const apiData = data?.data?.data;

  const addMutation = useMutation({
    mutationFn: apiAddBillingHistory,
    onSuccess() {
      refetch();
    },
  });

  const addMultipleMutation = useMutation({
    mutationFn: apiAddBillingHistories,
    onSuccess() {
      refetch();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: apiDeleteBillingHistory,
    onSuccess() {
      refetch();
    },
  });

  const columns = useMemo(() => {
    return getColumns({
      context: type as AppContext,
      detailContext,
      apiData: apiData as BillingHistory[],
      params,
      messages,
      navigate,
      deleteQuery: deleteMutation,
      userRole: role,
    });
  }, [apiData, params, type, role]);

  useEffect(() => {
    if (
      addMutation.isSuccess ||
      addMultipleMutation.isSuccess ||
      deleteMutation.isSuccess
    ) {
      toggle(false);
    }
  }, [
    addMutation.isSuccess,
    addMultipleMutation.isSuccess,
    deleteMutation.isSuccess,
  ]);

  const onPageChange = (page: number, limit: number) => {
    if (page !== params.page || limit !== params.limit) {
      setParams({ page, limit });
    }
  };

  const onChange: TableProps<any>["onChange"] = (
    pagination,
    filters,
    sorter,
  ) => {
    handleTableChange({ pagination, filters, sorter, params, setParams });
  };

  const filters = useMemo(() => {
    if (!params?.filter) return [];

    return params?.filter
      .map((f) => ({
        key: f.split("||")[0],
        title: columns.find((c) => c?.key === f.split("||")[0])?.title || "",
        operator: f.split("||")[1],
        value: f.split("||")[2],
      }))
      .filter((f) => !["customerId"].includes(f.key)) as TableHeaderFilter[];
  }, [params, state]);

  const [openModal, setModalOpen] = useState(false);
  const toggle = (status?: boolean) => {
    if (typeof status !== "undefined") setModalOpen(status);
    else setModalOpen(!openModal);
  };

  // Context-specific title and breadcrumb
  const title =
    customTitle || (messages["sidebar.blockchainPaymentRequest"] as string);

  return (
    <AppsContainer
      fullView={true}
      title={title}
      noContainer={withoutContainer}
      cardProps={{
        borderFull: true,
        coloredHeader: true,
      }}
    >
      <AppsHeader>
        <AppTableContentHeader
          showInfo
          page={params?.page || 1}
          perPage={params?.limit || 10}
          onPageChange={onPageChange}
          query={query}
          filters={filters}
          handleRemoveFilters={handleRemoveFilters}
          plusProps={{
            onClick: () => toggle(true),
            disabled: isLoading,
            loading: isLoading,
          }}
        />
      </AppsHeader>
      <AppsContent>
        <AppTable
          dataSource={apiData}
          columns={columns}
          rowKey={"id"}
          loading={isLoading || isFetching}
          onChange={onChange}
          minusHeight={minusHeight}
        />
      </AppsContent>

      <BillingPaymentRequestModal
        openModal={openModal}
        toggleModal={toggle}
        mutationQuery={addMutation}
        addMultipleQuery={addMultipleMutation}
        customer={customer}
      />
    </AppsContainer>
  );
};

export default SharedBlockchainPaymentRequestList;
